import React, { FC, useEffect, useRef, useState, useCallback } from 'react'
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.min.mjs'
import { useMediaQuery } from 'react-responsive'
import Loader from '../../../../components/common/Loader'
import PasswordProtectedFile from './PasswordProtectedFile'
import { Input } from '../../../../components/ui'
import { useRenderScheduler } from '../../../../hooks/file/useRenderScheduler'
import {
	getFileRenderingError,
	isPasswordProtectedPdf,
	shouldTryImagePreview,
} from '../../../../hooks/file/previewErrors'

pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.legacy.mjs`

interface MobileAppPreviewProps {
	downloadUrl: string
	isChatOpen?: boolean
}

const MobileAppPreview: FC<MobileAppPreviewProps> = ({
	downloadUrl,
	isChatOpen,
}) => {
	const isMobile = useMediaQuery({ maxWidth: 800 })
	const containerRef = useRef<HTMLDivElement>(null)
	const pageRefsRef = useRef<Map<number, HTMLDivElement>>(new Map())
	const isScrollingRef = useRef(false)
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		if (isChatOpen) {
			document.body.classList.add('zoho-widget-hidden')
		} else {
			document.body.classList.remove('zoho-widget-hidden')
		}
		return () => {
			document.body.classList.remove('zoho-widget-hidden')
		}
	}, [isChatOpen])

	const [error, setError] = useState<string | null>(null)
	const [isPasswordProtected, setIsPasswordProtected] = useState(false)
	const unlockRef = useRef<(password: string) => Promise<string | null>>(null)
	const [pdfDoc, setPdfDoc] = useState<any>(null)
	const [numPages, setNumPages] = useState(0)
	const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set())
	const [currentPage, setCurrentPage] = useState(1)
	const [inputPage, setInputPage] = useState('1')
	const renderedPagesRef = useRef<Set<number>>(new Set())
	const inFlightPagesRef = useRef<Set<number>>(new Set())
	const pendingRangeRef = useRef<{ startPage: number; endPage: number } | null>(
		null,
	)
	const isProcessingRenderRef = useRef(false)
	const loadingTaskRef = useRef<ReturnType<typeof pdfjs.getDocument> | null>(
		null,
	)
	const activePdfRef = useRef<any>(null)

	const releasePdfTask = useCallback(() => {
		activePdfRef.current = null
		const task = loadingTaskRef.current
		loadingTaskRef.current = null
		if (task) void task.destroy().catch((): void => undefined)
	}, [])

	const { scheduleRender } = useRenderScheduler({ isMobile, debounceMs: 600 })

	const renderPage = async (
		pdf: any,
		pageNum: number,
		container: HTMLDivElement,
	) => {
		if (activePdfRef.current !== pdf) return false
		if (
			renderedPagesRef.current.has(pageNum) ||
			inFlightPagesRef.current.has(pageNum)
		)
			return true
		inFlightPagesRef.current.add(pageNum)

		try {
			const page = await pdf.getPage(pageNum)
			if (activePdfRef.current !== pdf) return false

			const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
			const MAX_PIXELS = isIOS || isMobile ? 5_000_000 : 30_000_000

			const getSafeViewport = (page: any, desiredScale: number) => {
				const v = page.getViewport({ scale: desiredScale })
				const pixels = v.width * v.height
				if (pixels <= MAX_PIXELS) return v
				const k = Math.sqrt(MAX_PIXELS / pixels)
				return page.getViewport({ scale: desiredScale * k })
			}

			const desiredScale = isMobile ? 1.5 : 2
			const viewport = getSafeViewport(page, desiredScale)

			const pageWrapper = pageRefsRef.current.get(pageNum)
			if (!pageWrapper) return false

			const canvas = document.createElement('canvas')
			const context = canvas.getContext('2d')!

			canvas.width = Math.floor(viewport.width)
			canvas.height = Math.floor(viewport.height)
			canvas.style.width = '100%'
			canvas.style.height = 'auto'
			canvas.style.display = 'block'
			context.fillStyle = 'white'
			context.fillRect(0, 0, canvas.width, canvas.height)

			await page.render({
				canvasContext: context,
				viewport: viewport,
			}).promise
			if (activePdfRef.current !== pdf) return false

			pageWrapper.innerHTML = ''
			pageWrapper.appendChild(canvas)
			setRenderedPages((prev) => {
				const next = new Set(prev).add(pageNum)
				renderedPagesRef.current = next
				return next
			})
			return true
		} catch (err) {
			if (activePdfRef.current !== pdf) return false
			console.error('[pdfjs] Error rendering page', pageNum, err)
			setError(getFileRenderingError(err))
			setIsLoading(false)
			container.innerHTML = ''
			pageRefsRef.current.clear()
			pendingRangeRef.current = null
			releasePdfTask()
			return false
		} finally {
			if (activePdfRef.current === pdf) inFlightPagesRef.current.delete(pageNum)
		}
	}

	const processLatestRenderRange = useCallback(async (pdf: any) => {
		if (activePdfRef.current !== pdf || isProcessingRenderRef.current) return
		isProcessingRenderRef.current = true

		try {
			while (pendingRangeRef.current) {
				const range = pendingRangeRef.current
				pendingRangeRef.current = null

				if (!containerRef.current) break

				for (
					let pageNum = range.startPage;
					pageNum <= range.endPage;
					pageNum++
				) {
					if (pendingRangeRef.current) {
						// Newer scroll target arrived, prioritize latest range.
						break
					}

					try {
						if (!(await renderPage(pdf, pageNum, containerRef.current))) return
					} catch (err) {
						console.error(
							'[pdfjs] renderPageBatch failed at page',
							pageNum,
							err,
						)
						break
					}
				}
			}
		} finally {
			if (activePdfRef.current === pdf) {
				isProcessingRenderRef.current = false
				if (pendingRangeRef.current) {
					void processLatestRenderRange(pdf)
				}
			}
		}
	}, [])

	const renderPageBatch = useCallback(
		(pdf: any, startPage: number, endPage: number) => {
			const safeStart = Math.max(1, startPage)
			const safeEnd = Math.min(endPage, numPages)
			if (safeStart > safeEnd) return Promise.resolve()

			pendingRangeRef.current = { startPage: safeStart, endPage: safeEnd }
			return processLatestRenderRange(pdf)
		},
		[numPages, processLatestRenderRange],
	)

	const scrollToPage = useCallback((pageNum: number) => {
		const pageWrapper = pageRefsRef.current.get(pageNum)
		if (!pageWrapper) return

		isScrollingRef.current = true
		pageWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' })

		setTimeout(() => {
			isScrollingRef.current = false
		}, 500)

		setCurrentPage(pageNum)
		setInputPage(String(pageNum))
	}, [])

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		if (!pdfDoc || isScrollingRef.current) return

		const container = e.currentTarget
		const scrollTop = container.scrollTop
		const containerRect = container.getBoundingClientRect()
		const viewportHeight = containerRect.height

		const visiblePages: number[] = []
		pageRefsRef.current.forEach((ref, pageNum) => {
			const rect = ref.getBoundingClientRect()
			const relativeTop = rect.top - containerRect.top + scrollTop
			const relativeBottom = rect.bottom - containerRect.top + scrollTop

			if (
				relativeTop < scrollTop + viewportHeight &&
				relativeBottom > scrollTop
			) {
				visiblePages.push(pageNum)
			}
		})

		if (numPages >= 5 && visiblePages.length > 0) {
			const newCurrentPage = Math.min(...visiblePages)
			if (newCurrentPage !== currentPage) {
				setCurrentPage(newCurrentPage)
				setInputPage(String(newCurrentPage))
			}
		}

		if (visiblePages.length > 0) {
			scheduleRender(() => {
				if (isMobile) {
					const focusedPage = Math.min(...visiblePages)
					const startPage = Math.max(1, focusedPage - 1)
					const endPage = Math.min(numPages, focusedPage + 1)
					renderPageBatch(pdfDoc, startPage, endPage)
				} else {
					const startPage = Math.min(...visiblePages)
					const endPage = Math.min(startPage + 5, numPages)
					renderPageBatch(pdfDoc, startPage, endPage)
				}
			})
		}
	}

	useEffect(() => {
		return () => {
			inFlightPagesRef.current.clear()
			pendingRangeRef.current = null
			isProcessingRenderRef.current = false
		}
	}, [])

	useEffect(() => {
		renderedPagesRef.current = renderedPages
	}, [renderedPages])

	const handlePageInput = useCallback((value: string) => {
		setInputPage(value)
	}, [])

	const handlePageSubmit = useCallback(() => {
		const pageNum = parseInt(inputPage, 10)
		if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= numPages) {
			scrollToPage(pageNum)
		} else {
			setInputPage(String(currentPage))
		}
	}, [inputPage, numPages, currentPage, scrollToPage])

	useEffect(() => {
		let cancelled = false
		const renderDocument = async (
			password?: string,
		): Promise<string | null> => {
			if (!downloadUrl || !containerRef.current)
				return getFileRenderingError(null)
			releasePdfTask()

			try {
				setIsLoading(true)
				if (password === undefined) {
					setError(null)
					setIsPasswordProtected(false)
				}
				setNumPages(0)
				setPdfDoc(null)
				containerRef.current.innerHTML = ''
				pageRefsRef.current.clear()
				setRenderedPages(new Set())
				renderedPagesRef.current = new Set()
				inFlightPagesRef.current.clear()
				pendingRangeRef.current = null
				isProcessingRenderRef.current = false

				try {
					loadingTaskRef.current = pdfjs.getDocument(
						password === undefined
							? downloadUrl
							: { url: downloadUrl, password },
					)
					const pdf = await loadingTaskRef.current.promise
					if (cancelled) return null
					activePdfRef.current = pdf
					setError(null)
					setIsPasswordProtected(false)
					const container = containerRef.current
					if (!container) return null

					// Use the first page ratio so loading placeholders keep realistic PDF height.
					let pageAspectRatio = '1 / 1.414'
					try {
						const firstPage = await pdf.getPage(1)
						const firstViewport = firstPage.getViewport({ scale: 1 })
						pageAspectRatio = `${firstViewport.width} / ${firstViewport.height}`
					} catch {
						// Keep default A-series paper ratio fallback.
					}
					if (cancelled) return null

					setPdfDoc(pdf)
					setNumPages(pdf.numPages)
					container.innerHTML = ''

					for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
						const pageWrapper = document.createElement('div')
						pageWrapper.style.marginBottom = '8px'
						pageWrapper.style.width = 'calc(100% - 32px)'
						pageWrapper.style.maxWidth = '100%'
						pageWrapper.style.backgroundColor = 'white'
						pageWrapper.style.borderRadius = '4px'
						pageWrapper.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
						pageWrapper.style.aspectRatio = pageAspectRatio
						pageWrapper.style.minHeight = '0'
						pageWrapper.style.display = 'flex'
						pageWrapper.style.alignItems = 'center'
						pageWrapper.style.justifyContent = 'center'
						pageWrapper.style.color = '#999'
						pageWrapper.style.overflow = 'hidden'
						pageWrapper.style.boxSizing = 'border-box'

						const loader = document.createElement('div')
						loader.className =
							'animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400'
						pageWrapper.appendChild(loader)

						container.appendChild(pageWrapper)
						pageRefsRef.current.set(pageNum, pageWrapper)
					}

					setPdfDoc(pdf)
					setNumPages(pdf.numPages)

					// Force render first pages immediately
					const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
					const firstBatch = isIOS || isMobile ? 3 : 10
					const endPage = Math.min(firstBatch, pdf.numPages)

					for (let pageNum = 1; pageNum <= endPage; pageNum++) {
						if (cancelled) return null
						try {
							if (!(await renderPage(pdf, pageNum, container))) {
								return getFileRenderingError(null)
							}
						} catch (err) {
							console.error(
								'[pdfjs] Error rendering initial page',
								pageNum,
								err,
							)
						}
					}

					setIsLoading(false)
				} catch (pdfError) {
					if (cancelled) return null
					releasePdfTask()
					if (!shouldTryImagePreview(pdfError)) throw pdfError
					const img = new Image()
					img.crossOrigin = 'anonymous'

					img.onload = () => {
						if (cancelled) return
						if (containerRef.current) {
							const canvas = document.createElement('canvas')
							const ctx = canvas.getContext('2d')!
							canvas.width = img.naturalWidth || img.width
							canvas.height = img.naturalHeight || img.height
							canvas.style.width = '100%'
							canvas.style.height = 'auto'
							ctx.drawImage(img, 0, 0)

							containerRef.current.innerHTML = ''
							containerRef.current.appendChild(canvas)
							setIsLoading(false)
						}
					}

					img.onerror = () => {
						if (cancelled) return
						setError('Unable to load document')
						setIsLoading(false)
					}

					img.src = downloadUrl
				}
				return null
			} catch (err) {
				if (cancelled) return null
				const message = getFileRenderingError(err)
				setIsPasswordProtected(isPasswordProtectedPdf(err))
				setError(message)
				setIsLoading(false)
				return message
			}
		}

		unlockRef.current = renderDocument
		renderDocument()
		let timeoutId: ReturnType<typeof setTimeout> | null = null
		if (!containerRef.current && downloadUrl) {
			timeoutId = setTimeout(renderDocument, 100)
		}
		return () => {
			cancelled = true
			releasePdfTask()
			unlockRef.current = null
			if (timeoutId) clearTimeout(timeoutId)
		}
	}, [downloadUrl, releasePdfTask])

	return (
		<div className="w-full h-full relative">
			{error && (
				<div
					role="alert"
					className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 p-4 text-center text-text-main dark:text-text-main-dark"
				>
					{isPasswordProtected ? (
						<PasswordProtectedFile
							key={downloadUrl}
							message={error}
							onUnlock={(password) =>
								unlockRef.current?.(password) ??
								Promise.resolve(getFileRenderingError(null))
							}
						/>
					) : (
						<span>{error}</span>
					)}
				</div>
			)}
			<div
				className="w-full h-full overflow-y-auto overflow-x-hidden bg-background-light dark:bg-background-light-dark"
				style={{ WebkitOverflowScrolling: 'touch' }}
				onScroll={handleScroll}
			>
				{isLoading && !error && (
					<div className="absolute inset-0 flex items-center justify-center">
						<Loader size={30} />
					</div>
				)}
				<div
					ref={containerRef}
					className="flex flex-col items-center py-4 gap-2 w-full"
				/>
			</div>

			{/* Pagination */}
			{numPages >= 5 && !isLoading && !error && !isChatOpen && (
				<div className="animate-appearing group hover:opacity-100 hover:bg-black/90 duration-default absolute w-fit min-w-20 h-10 rounded-lg left-1/2 transform -translate-x-1/2 text-white z-30 bottom-4 flex items-center justify-center gap-2 py-2 px-4 opacity-80 bg-black/60">
					Page
					<Input
						type="number"
						min={1}
						max={numPages}
						value={inputPage}
						onChange={(e) => handlePageInput(e.target.value)}
						onBlur={handlePageSubmit}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handlePageSubmit()
								e.preventDefault()
							}
						}}
						wrapperClassName="w-14 h-8"
						inputClassName="bg-transparent text-white px-1 text-center"
						style={{ fontSize: '16px' }}
					/>
					of {numPages}
				</div>
			)}
		</div>
	)
}

export default MobileAppPreview
